/**
 * Z7 call-out band (09 §1.1): one call-out at a time, priority queue with
 * pre-emption (higher wins; equal → newer replaces). Pure.
 */
import { BETWEEN_ROUNDS_S, HUB_SPAWN_INVULN_S, ROUND_END_FREEZE_TICKS, SIM_HZ } from '../../../sim/constants.js'
import type { Team } from '../../../sim/types.js'
import type { SimEvent } from '../../../shared/events.js'

export interface CalloutDef { size: number; durationMs: number; priority: number; audio?: string }

/** The 09 §1.1 catalogue, as data. `durationMs: Infinity` = until replaced/cleared. */
export const CALLOUTS = {
  count: { size: 96, durationMs: 1000, priority: 9, audio: 'count_tick' },
  go: { size: 120, durationMs: 600, priority: 9, audio: 'count_go' },
  roundResult: { size: 48, durationMs: (ROUND_END_FREEZE_TICKS / SIM_HZ + BETWEEN_ROUNDS_S) * 1000, priority: 8, audio: 'round_end_sting' },
  matchResult: { size: 64, durationMs: Infinity, priority: 10, audio: 'match_end_sting' },
  overtime: { size: 40, durationMs: 2500, priority: 7, audio: 'overtime_sting' },
  doubleDerez: { size: 40, durationMs: 2500, priority: 8, audio: 'void_round_sting' },
  voidRound: { size: 40, durationMs: 2500, priority: 8, audio: 'void_round_sting' },
  coin: { size: 40, durationMs: 3000, priority: 8, audio: 'coin_land' },
  protected: { size: 16, durationMs: HUB_SPAWN_INVULN_S * 1000, priority: 2 },
  training: { size: 24, durationMs: Infinity, priority: 3, audio: 'ui_card_select' },
  trainingDone: { size: 40, durationMs: 2000, priority: 8, audio: 'ui_tutorial_step' },
  pause: { size: 40, durationMs: Infinity, priority: 6 },
} as const satisfies Record<string, CalloutDef>

export type CalloutKind = keyof typeof CALLOUTS

export interface Callout { id: string; kind: CalloutKind; text: string; size: number; durationMs: number; priority: number; sub?: string; team?: Team | null; at: number }
export interface ActiveCallout extends Callout { shownAt: number; progress: number }

export const PEN_REVEAL_MS = 80

export function makeCallout(kind: CalloutKind, text: string, at: number, extra: { sub?: string; team?: Team | null; id?: string; durationMs?: number } = {}): Callout {
  const d = CALLOUTS[kind]
  return { id: extra.id ?? `${kind}:${at}`, kind, text, size: d.size, durationMs: extra.durationMs ?? d.durationMs, priority: d.priority, sub: extra.sub, team: extra.team, at }
}

export class CalloutQueue {
  private active: ActiveCallout | null = null
  private queue: Callout[] = []

  push(c: Callout): void {
    const cur = this.active
    if (!cur || c.priority >= cur.priority) {
      // Pre-empt (higher) or replace (equal → newer). The displaced one is dropped, not resumed.
      this.active = { ...c, shownAt: c.at, progress: 0 }
      return
    }
    // Lower priority while something stronger shows: queue it, newest of its priority only.
    this.queue = this.queue.filter((q) => q.priority !== c.priority)
    this.queue.push(c)
    this.queue.sort((a, b) => b.priority - a.priority || b.at - a.at)
  }

  /** Remove a call-out (or all of a kind), e.g. match result → results screen. */
  clear(kind?: CalloutKind): void {
    if (!kind) { this.active = null; this.queue = []; return }
    if (this.active?.kind === kind) this.active = null
    this.queue = this.queue.filter((q) => q.kind !== kind)
  }

  current(now: number, reducedMotion = false): ActiveCallout | null {
    if (this.active && now >= this.active.shownAt + this.active.durationMs) this.active = null
    while (!this.active && this.queue.length) {
      const next = this.queue.shift()!
      // Queued items expire relative to their own timestamp.
      if (now >= next.at + next.durationMs) continue
      this.active = { ...next, shownAt: now, progress: 0 }
    }
    if (!this.active) return null
    this.active.progress = reducedMotion ? 1 : Math.min(1, (now - this.active.shownAt) / PEN_REVEAL_MS)
    return this.active
  }
}

export interface CalloutCtx { localTeam: Team | null; spectator: boolean; now: number; decidedRounds: number; hub?: boolean; localId?: string }

const reasonText = (reason: string, score?: [number, number]): string | undefined => {
  switch (reason) {
    case 'ladder_round_elims': return `TIEBREAK: ELIMINATIONS${score ? ` ${score[0]}–${score[1]}` : ''}`
    case 'ladder_perfects': return 'TIEBREAK: PERFECT DEFLECTS'
    case 'ladder_match_elims': return 'TIEBREAK: MATCH ELIMINATIONS'
    case 'timer_living': return 'TIME — MORE STANDING'
    case 'forfeit': return 'FORFEIT'
    default: return undefined
  }
}

/** Map a sim event to zero or more call-outs (09 §1.1 table). */
export function fromSimEvent(e: SimEvent, c: CalloutCtx): Callout[] {
  const now = c.now
  switch (e.type) {
    case 'countdown': return [e.n === 0 ? makeCallout('go', 'GO', now) : makeCallout('count', String(e.n), now)]
    case 'overtime': return [makeCallout('overtime', 'OVERTIME — DECK CLOSING', now)]
    case 'roundEnd': {
      if (e.void) return [e.reason === 'double_ko' ? makeCallout('doubleDerez', 'DOUBLE DEREZ — VOID ROUND', now) : makeCallout('voidRound', 'VOID ROUND — REPLAY', now)]
      if (e.reason === 'coin') return [makeCallout('coin', `TIEBREAK: COIN — TEAM ${e.winner === 0 ? 'A' : 'B'}`, now, { team: e.winner })]
      return [makeCallout('roundResult', `ROUND ${c.decidedRounds} — TEAM ${e.winner === 0 ? 'A' : 'B'}`, now, { team: e.winner, sub: reasonText(e.reason) })]
    }
    case 'coin': return [makeCallout('coin', `TIEBREAK: COIN — TEAM ${e.result === 0 ? 'A' : 'B'}`, now, { team: e.result })]
    case 'matchEnd': {
      if (e.winner === null) return [makeCallout('matchResult', 'MATCH ABANDONED', now)]
      const own = !c.spectator && c.localTeam !== null
      const txt = own ? (e.winner === c.localTeam ? 'VICTORY' : 'DEFEAT') : `TEAM ${e.winner === 0 ? 'A' : 'B'} WINS`
      return [makeCallout('matchResult', txt, now, { team: e.winner, sub: `${e.score[0]} – ${e.score[1]}${e.coinUsed ? ' · COIN' : ''}` })]
    }
    case 'respawn': return c.hub && c.localId === e.playerId ? [makeCallout('protected', 'PROTECTED', now)] : []
    default: return []
  }
}
